import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CookieConsent {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CookieService {
  private consentSubject = new BehaviorSubject<CookieConsent | null>(null);
  public consent$ = this.consentSubject.asObservable();

  constructor() {
    this.loadConsent();
  }

  private loadConsent(): void {
    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      this.consentSubject.next(JSON.parse(consent));
    }
  }

  getConsent(): CookieConsent | null {
    return this.consentSubject.value;
  }

  hasConsent(): boolean {
    return this.getConsent() !== null;
  }

  hasAnalyticsConsent(): boolean {
    const consent = this.getConsent();
    return consent ? consent.analytics : false;
  }

  hasMarketingConsent(): boolean {
    const consent = this.getConsent();
    return consent ? consent.marketing : false;
  }

  hasFunctionalConsent(): boolean {
    const consent = this.getConsent();
    return consent ? consent.functional : false;
  }

  updateConsent(consent: CookieConsent): void {
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    this.consentSubject.next(consent);
  }

  revokeConsent(): void {
    localStorage.removeItem('cookie-consent');
    localStorage.removeItem('cookie-consent-date');
    this.consentSubject.next(null);
  }

  // Utility methods for checking specific cookie types
  canUseAnalytics(): boolean {
    return this.hasAnalyticsConsent();
  }

  canUseMarketing(): boolean {
    return this.hasMarketingConsent();
  }

  canUseFunctional(): boolean {
    return this.hasFunctionalConsent();
  }

  // Get consent date
  getConsentDate(): Date | null {
    const dateString = localStorage.getItem('cookie-consent-date');
    return dateString ? new Date(dateString) : null;
  }

  // Check if consent is expired (optional - for GDPR compliance)
  isConsentExpired(days: number = 365): boolean {
    const consentDate = this.getConsentDate();
    if (!consentDate) return true;
    
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - consentDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > days;
  }
}
